/**
 * Auth API client for Scratch Clone backend.
 * Uses httpOnly cookies (set by backend), no manual token management needed.
 */

const API_BASE = '/api';

let _refreshing = null;

const authApi = {
    /**
     * Wrapper that retries a request once after refreshing the access token.
     * Prevents multiple concurrent refresh calls.
     */
    async _fetchWithRefresh (url, options) {
        let response = await fetch(url, {...options, credentials: 'include'});
        if (response.status === 401) {
            if (!_refreshing) {
                _refreshing = fetch(`${API_BASE}/auth/refresh`, {
                    method: 'POST',
                    credentials: 'include'
                }).finally(() => { _refreshing = null; });
            }
            const refreshResp = await _refreshing;
            if (refreshResp.ok) {
                response = await fetch(url, {...options, credentials: 'include'});
            }
        }
        return response;
    },

    async register ({username, email, password, displayName}) {
        const response = await fetch(`${API_BASE}/auth/register`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            credentials: 'include',
            body: JSON.stringify({username, email, password, displayName})
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || 'Registration failed');
        }
        if (data.pending) {
            return {pending: true, message: data.message};
        }
        return data.user;
    },

    async login ({username, password}) {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            credentials: 'include',
            body: JSON.stringify({username, password})
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || 'Login failed');
        }
        return data.user;
    },

    async logout () {
        await fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            credentials: 'include'
        });
    },

    async me () {
        const response = await this._fetchWithRefresh(`${API_BASE}/auth/me`, {});
        if (!response.ok) return null;
        const data = await response.json();
        return data.user;
    },

    async refresh () {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
        });
        return response.ok;
    },

    async updateLocale (locale) {
        const response = await this._fetchWithRefresh(`${API_BASE}/auth/me/locale`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({locale})
        });
        return response.ok;
    },

    async getGuestAccess () {
        try {
            const response = await fetch(`${API_BASE}/auth/guest-access`);
            if (!response.ok) return true; // allow by default on error
            const data = await response.json();
            return data.allowGuests;
        } catch {
            return true; // allow by default on error
        }
    },

    async getRegistrationInfo () {
        try {
            const response = await fetch(`${API_BASE}/auth/registration-info`);
            if (!response.ok) return {requireApproval: false};
            return await response.json();
        } catch {
            return {requireApproval: false};
        }
    }
};

export default authApi;
